import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Shield,
  Settings,
  Users,
  BarChart3,
  Database,
  Activity,
  FileText,
  Globe,
  Lock,
  Zap,
  ArrowLeft,
  Bell,
  Search,
  HelpCircle,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAdminPermissions } from '@/hooks/useAdminPermissions';

interface AdminLayoutProps {
  children: React.ReactNode;
  title?: string;
  description?: string;
}

interface AdminNavItem {
  title: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  description: string;
  permission?: string;
  badge?: string;
  category: 'system' | 'organization' | 'users' | 'data';
}

const adminNavItems: AdminNavItem[] = [
  // System Administration
  {
    title: 'Dashboard',
    href: '/app/admin',
    icon: BarChart3,
    description: 'System overview and metrics',
    category: 'system',
  },
  {
    title: 'System Health',
    href: '/app/admin/system',
    icon: Activity,
    description: 'Monitor system performance',
    permission: 'admin.system.view',
    category: 'system',
  },
  {
    title: 'Background Jobs',
    href: '/app/admin/jobs',
    icon: Zap,
    description: 'Monitor background tasks',
    permission: 'admin.system.view',
    category: 'system',
  },

  // Organization Management
  {
    title: 'Organizations',
    href: '/app/admin/organizations',
    icon: Globe,
    description: 'Manage organizations',
    permission: 'admin.organizations.view',
    category: 'organization',
  },
  {
    title: 'Usage Analytics',
    href: '/app/admin/analytics',
    icon: BarChart3,
    description: 'Organization usage stats',
    permission: 'admin.analytics.view',
    category: 'organization',
  },

  // User Management
  {
    title: 'User Management',
    href: '/app/admin/users',
    icon: Users,
    description: 'Manage all users',
    permission: 'admin.users.view',
    category: 'users',
  },
  {
    title: 'Permissions',
    href: '/app/admin/permissions',
    icon: Lock,
    description: 'Manage roles & permissions',
    permission: 'admin.permissions.view',
    category: 'users',
  },
  {
    title: 'Audit Logs',
    href: '/app/admin/audit',
    icon: FileText,
    description: 'View system audit logs',
    permission: 'admin.audit.view',
    category: 'users',
  },

  // Data Management
  {
    title: 'Database Admin',
    href: '/app/admin/database',
    icon: Database,
    description: 'Database administration',
    permission: 'admin.database.view',
    category: 'data',
  },
  {
    title: 'Import Manager',
    href: '/app/admin/imports',
    icon: FileText,
    description: 'Manage data imports',
    permission: 'admin.imports.view',
    category: 'data',
  },
  {
    title: 'System Settings',
    href: '/app/admin/settings',
    icon: Settings,
    description: 'Global system configuration',
    permission: 'admin.settings.manage',
    category: 'data',
  },
];

const categoryLabels = {
  system: 'System Administration',
  organization: 'Organization Management',
  users: 'User Management',
  data: 'Data Management',
};

const categoryIcons = {
  system: Activity,
  organization: Globe,
  users: Users,
  data: Database,
};

export function AdminLayout({ children, title, description }: AdminLayoutProps) {
  const { user } = useAuth();
  const { hasPermission } = useAdminPermissions();
  const location = useLocation();
  const navigate = useNavigate();

  // Check if user has admin access - only Django staff users can access system admin
  if (!user?.is_staff) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <Shield className="mx-auto mb-4 size-12 text-muted-foreground" />
            <CardTitle>System Admin Access Required</CardTitle>
          </CardHeader>
          <CardContent className="text-center">
            <p className="mb-4 text-muted-foreground">
              You need Django staff privileges to access the system admin panel. Organization admins
              should use the organization settings instead.
            </p>
            <Button onClick={() => navigate('/app')} variant="outline">
              <ArrowLeft className="mr-2 size-4" />
              Back to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Filter nav items based on permissions
  const visibleNavItems = adminNavItems.filter(
    item => !item.permission || hasPermission(item.permission)
  );

  // Group items by category
  const groupedNavItems = visibleNavItems.reduce(
    (groups, item) => {
      if (!groups[item.category]) {
        groups[item.category] = [];
      }
      groups[item.category].push(item);
      return groups;
    },
    {} as Record<string, AdminNavItem[]>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Admin Header */}
      <header className="sticky top-0 z-40 border-b border-gray-200 bg-white">
        <div className="flex items-center justify-between px-6 py-4">
          <div className="flex items-center space-x-4">
            <Link
              to="/app"
              className="flex items-center text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="mr-2 size-4" />
              Back to App
            </Link>
            <Separator orientation="vertical" className="h-6" />
            <div className="flex items-center space-x-3">
              <Shield className="size-6 text-blue-600" />
              <div>
                <h1 className="text-lg font-semibold text-foreground">Admin Panel</h1>
                <p className="text-xs text-muted-foreground">System Administration</p>
              </div>
            </div>
          </div>

          <div className="flex items-center space-x-4">
            <Button variant="ghost" size="sm">
              <Search className="size-4" />
            </Button>
            <Button variant="ghost" size="sm">
              <Bell className="size-4" />
            </Button>
            <Button variant="ghost" size="sm">
              <HelpCircle className="size-4" />
            </Button>
            <Badge variant="secondary" className="bg-blue-100 text-blue-800">
              Staff
            </Badge>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Admin Sidebar */}
        <aside className="min-h-[calc(100vh-73px)] w-64 border-r border-gray-200 bg-white">
          <ScrollArea className="h-full">
            <div className="space-y-6 p-4">
              {Object.entries(groupedNavItems).map(([category, items]) => {
                const CategoryIcon = categoryIcons[category as keyof typeof categoryIcons];

                return (
                  <div key={category}>
                    <div className="mb-3 flex items-center space-x-2">
                      <CategoryIcon className="size-4 text-muted-foreground" />
                      <h3 className="text-sm font-medium uppercase tracking-wider text-muted-foreground">
                        {categoryLabels[category as keyof typeof categoryLabels]}
                      </h3>
                    </div>

                    <div className="space-y-1">
                      {items.map(item => {
                        const isActive = location.pathname === item.href;
                        const Icon = item.icon;

                        return (
                          <Link
                            key={item.href}
                            to={item.href}
                            className={cn(
                              'flex items-center space-x-3 px-3 py-2 rounded-lg text-sm transition-colors',
                              isActive
                                ? 'bg-blue-100 text-blue-900 font-medium'
                                : 'text-gray-700 hover:bg-gray-100'
                            )}
                          >
                            <Icon
                              className={cn(
                                'h-4 w-4',
                                isActive ? 'text-blue-600' : 'text-gray-500'
                              )}
                            />
                            <div className="flex-1">
                              <div className="flex items-center justify-between">
                                <span>{item.title}</span>
                                {item.badge && (
                                  <Badge variant="secondary" className="text-xs">
                                    {item.badge}
                                  </Badge>
                                )}
                              </div>
                              <p className="mt-0.5 text-xs text-muted-foreground">
                                {item.description}
                              </p>
                            </div>
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </ScrollArea>
        </aside>

        {/* Main Content */}
        <main className="flex-1 overflow-auto">
          <div className="p-6">
            {title && (
              <div className="mb-6">
                <h1 className="text-2xl font-bold text-foreground">{title}</h1>
                {description && <p className="mt-1 text-muted-foreground">{description}</p>}
              </div>
            )}
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}
